import {Product} from "../../../rest/jtmc/models/Product";
import {Colors} from "../colors";
import React, {useContext, useRef, useState} from "react";
import AppConfigContext from "../../../contexts/AppConfigContext";
import {CartContext} from "../../../contexts/CartContext";
import {Animated, Dimensions, Image, TouchableOpacity, TouchableWithoutFeedback, View} from "react-native";
import {hexToRgbA} from "../../../helpers";
import Text from "./Text";
import {ColumnSpace, RowSpace} from "./_CommonComponents";
import FontAwesome from "./FontAwesome";
import Selective from "../../../rest/jtmc/models/Selective";
import {RadioGroup} from "./RadioCheck";
import Additive from "../../../rest/jtmc/models/Additive";
import {CheckBox} from "./CheckBox";
import Button from "./Button";
import {productItemImageWidth, ProductItemStyles} from "../styles";
import DiscountIcon from "../assets/DiscountIcon";
import {Variant} from "../../../rest/jtmc/models/Variant";

const ProductItem = React.memo(({product, orderable}: { product: Product, orderable: boolean }) => {

    const colors = Colors.instance(useContext(AppConfigContext).theme, useContext(AppConfigContext).color);
    const cart = useContext(CartContext);

    const variantListAnimation = useRef(new Animated.ValueXY({x: 0, y: 0})).current;
    const [variantListOpened, setVariantListOpened] = useState(false);

    const openVariantList = () => {
        setVariantListOpened(true);
        Animated.timing(variantListAnimation, {
            toValue: {x: 100, y: 100},
            duration: 200,
            useNativeDriver: false
        }).start();
    }
    const closeVariantList = () => {
        setVariantListOpened(false);
        Animated.timing(variantListAnimation, {
            toValue: {x: 0, y: 0},
            duration: 200,
            useNativeDriver: false
        }).start();
    }

    const windowWidth = Dimensions.get('window').width;
    const lStyles = ProductItemStyles(colors);

    const scaleInterpolationConfig = {
        inputRange: [0, 100],
        outputRange: ["0%", "100%"]
    };
    const translateInterpolationConfigX = {
        inputRange: [0, 100],
        outputRange: [-(windowWidth / 2) + productItemImageWidth + 50, 0]
    };
    const translateInterpolationConfigY = {
        inputRange: [0, 100],
        outputRange: [-35, 0]
    };
    const buttonBottomBorderInterpolationConfig = {
        inputRange: [0, 100],
        outputRange: [14, 0]
    };
    const buttonTopBorderInterpolationConfig = {
        inputRange: [0, 100],
        outputRange: [14, 6]
    };
    const backColorInterpolate = {
        inputRange: [0, 100],
        outputRange: [hexToRgbA(colors.get("green") as string), hexToRgbA(colors.get("lowConBackground") as string)]
    };
    const heightInterpolate = {
        inputRange: [0, 50, 100],
        outputRange: [0, 200, 1000]
    };

    const interpolatedButtonBottomBorder = variantListAnimation.x.interpolate(buttonBottomBorderInterpolationConfig);
    const interpolatedButtonTopBorder = variantListAnimation.x.interpolate(buttonTopBorderInterpolationConfig);
    const interpolatedBackgroundColor = variantListAnimation.x.interpolate(backColorInterpolate);
    const interpolatedHeight = variantListAnimation.x.interpolate(heightInterpolate);

    const productAdderTransform = [
        {translateX: variantListAnimation.x.interpolate(translateInterpolationConfigX)},
        {translateY: variantListAnimation.y.interpolate(translateInterpolationConfigY)},
        {scaleX: variantListAnimation.x.interpolate(scaleInterpolationConfig)},
        {scaleY: variantListAnimation.y.interpolate(scaleInterpolationConfig)}
    ];

    const cartAddButton = () => {
        if (product.needsProductAdderDialog()) {
            if (variantListOpened)
                closeVariantList();
            else openVariantList();
        } else cart.addToCart(product, null, null, []);
    }

    const ConditionalImage = () => {
        return product.hasImage() ? <Image style={lStyles.image} source={{uri: product.images[0].url}}/> : <></>;
    }

    return <View>
        <TouchableWithoutFeedback onPress={() => {
            if (variantListOpened) {
                closeVariantList();
                setVariantListOpened(false);
            } else if (product.needsProductAdderDialog()) {
                openVariantList();
                setVariantListOpened(true);
            }
        }}>
            <View style={lStyles.mainContainer}>

                <View
                    style={[lStyles.detailsPartContainer, product.hasImage() ? {} : lStyles.detailsPartContainerNoImage]}>

                    <View style={{flexGrow: 1}}>
                        <Text size={16}>{product.fullName}</Text>
                        <ColumnSpace h={6}/>
                        <Text size={12} mb={10} weight={600}>{product.description}</Text>
                    </View>

                    <View style={lStyles.actionRowContainer}>
                        {/*todo: Edit price from branch config*/}
                        <Text weight={300} size={18}
                              style={lStyles.priceText}>{product.priceWithDiscount == 0 ? "بدون قیمت" : `${product.priceWithDiscount} تومان`}</Text>

                        {orderable ?
                            <View style={{flexDirection: "row"}}>

                                {/*<TouchableOpacity style={lStyles.deleteButton} >*/}
                                {/*    <FontAwesome icon={""} size={12} color={"white"}/>*/}
                                {/*</TouchableOpacity>*/}

                                <RowSpace w={9}/>
                                <Text size={14} weight={800}
                                      style={{alignSelf: "center"}}>{`${cart.getCount(product)}`}</Text>
                                <RowSpace w={9}/>

                                <Animated.View style={{
                                    overflow: "hidden",
                                    borderTopRightRadius: interpolatedButtonTopBorder,
                                    borderTopLeftRadius: interpolatedButtonTopBorder,
                                    borderBottomRightRadius: interpolatedButtonBottomBorder,
                                    borderBottomLeftRadius: interpolatedButtonBottomBorder,
                                }}>
                                    <TouchableOpacity disabled={cart.isFetching} style={lStyles.cartAddButton}
                                                      onPress={cartAddButton}>
                                        <RowSpace w={9}/>
                                        <FontAwesome icon={""} size={12} color={"white"}/>
                                        <RowSpace w={3}/>
                                        <FontAwesome icon={""} size={12} color={"white"}/>
                                        <RowSpace w={9}/>
                                    </TouchableOpacity>
                                </Animated.View>

                            </View> : <></>}

                    </View>
                </View>

                {product.hasImage() ? <>
                    <RowSpace w={4}/>
                    <ConditionalImage/>
                </> : <></>}

                {/*Move it in below*/}
                {/*Animate*/}
                {/*..*/}

                <DiscountView product={product}/>

            </View>

        </TouchableWithoutFeedback>

        <View style={{paddingHorizontal: 12}}>
            <Animated.View style={[lStyles.productAdderContainer, {maxHeight: interpolatedHeight}]}>
                <Animated.View
                    style={[lStyles.productAdderInsideContainer, {backgroundColor: interpolatedBackgroundColor}]}>
                    <ProductAdder orderable={orderable} product={product}/>
                </Animated.View>
            </Animated.View>
        </View>

    </View>
});

function DiscountView(props: { product: Product }) {
    const product = props.product;
    const colors = Colors.instance(useContext(AppConfigContext).theme, useContext(AppConfigContext).color);

    let discountText = "";
    if (product.hasActiveDiscount)
        discountText = `${product.price - product.priceWithDiscount} ت تخفیف`;

    return (product.hasActiveDiscount) ? <View style={{
        position: "absolute",
        overflow: "visible",
        left: -10,
        top: 10,
        transform: [
            {rotate: '-23deg'}
        ]
    }}>
        <DiscountIcon style={{
            position: "absolute",
            zIndex: 2,
            left: -5
        }}/>
        <View style={{
            position: "absolute",
            zIndex: 1,
            left: -15,
            top: 28,
            width: 100,
            justifyContent: "flex-end",
            flexDirection: "row"
        }}>
            <View style={{
                backgroundColor: "#fff",
                paddingHorizontal: 6,
                paddingVertical: 4,
                borderRadius: 8
            }}>

                <Text size={10} weight={800} color={colors.get("primaryColor") as string}>
                    {discountText}
                </Text>
            </View>
        </View>
    </View> : <></>;

}

function ProductAdder({product, orderable}: { product: Product, orderable: boolean }) {

    const colors = Colors.instance(useContext(AppConfigContext).theme, useContext(AppConfigContext).color);
    const windowWidth = Dimensions.get('window').width;
    const lStyles = ProductItemStyles(colors);
    const cartContext = useContext(CartContext);

    const [selectedVariant, setSelectedVariant] = useState<Variant>(product.variants[0]);
    const [selectedSelective, setSelectedSelective] = useState<Selective>(product.selectives[0]);
    const [selectedAdditives, setSelectedAdditives] = useState<Additive[]>([]);

    const onVariantSelect = (variant: Variant) => {
        setSelectedVariant(variant);
    }
    const onItemChange = (selective: Selective) => {
        setSelectedSelective(selective)
    }
    const onItemCheck = (additive: Additive) => {
        if (hasAdditve(additive)) removeAdditive(additive); else addAdditive(additive);
    }
    const addAdditive = (additive: Additive) => setSelectedAdditives(selectedAdditives.concat(additive));
    const removeAdditive = (additive: Additive) => setSelectedAdditives(selectedAdditives.map(item => {
        if (item != additive) return item;
    }) as Additive[]);
    const hasAdditve = (additive: Additive) => selectedAdditives.includes(additive);

    const variantsViews = product.variants.map((variant: Variant, index) => {
        const selected = variant == selectedVariant;
        return <TouchableOpacity key={index} style={{
            width: '50%',
            paddingEnd: (index % 2 == 0) ? 0 : 6,
            paddingStart: (index % 2 == 0) ? 6 : 0
        }} onPress={() => onVariantSelect(variant)}>
            <View style={[lStyles.variantContainer, {
                backgroundColor: !selected ? "#7D93FF20" : colors.get("green") as string,
            }]}>
                <Text size={13} weight={600} color={selected ? "#fff" : colors.get("highConText") as string}
                      style={lStyles.variantText}>{variant.name}</Text>
                <Text size={13} weight={600} color={selected ? "#fff" : colors.get("highConText") as string}
                      style={lStyles.variantText}>{variant.priceWithDiscount == 0 ? `بدون قیمت` : `${variant.priceWithDiscount} تومان`}</Text>
            </View>
        </TouchableOpacity>
    });

    return <>
        {product.hasVariants ? <>
            <Text>{orderable ? "نوع محصول را انتخاب کنید" : "نوع محصول"}</Text>
            <View style={lStyles.variantsContainer}>
                {variantsViews}
            </View>
            <ColumnSpace/>
        </> : <></>}

        {product.hasSelective() ? <>
            <Text>آیتم های قابل انتخاب</Text>
            <View style={lStyles.selectivesAndAdditiveContainer}>
                <RadioGroup<Selective> selected={selectedSelective} items={product.selectives.map((s, i) => {
                    return {
                        text: s.title,
                        value: s
                    }
                })} onItemChange={onItemChange}/>
            </View>
            <ColumnSpace/>
        </> : <></>}

        {product.hasAdditives() ? <>
            <Text>آیتم های اضافه</Text>
            <View style={lStyles.selectivesAndAdditiveContainer}>
                {product.additives.map((additive, index) => {
                    return <CheckBox text={additive.title} check={selectedAdditives.includes(additive)} onCheck={() => {
                        onItemCheck(additive);
                    }}/>
                })}
            </View>
            <ColumnSpace/>
        </> : <></>}

        {orderable ?
            <Button disabled={cartContext.isFetching}
                title={"افزودن"}
                color={colors.get("green") as string}
                onPress={() => {
                    cartContext.addToCart(product, selectedVariant, selectedSelective, selectedAdditives);
                }}/> : <></>}
    </>
}

export default ProductItem;